// ===========================================
// COMPONENTE: Lista de Auditoria
// ===========================================

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Loader2, Info } from 'lucide-react'
import type { AuditLogConUsuario } from '@/types/auditoria'
import { getAccionConfig, getEntidadConfig, formatearDetalleAuditoria } from '@/types/auditoria'

interface AuditoriaListProps {
  logs: AuditLogConUsuario[]
  isLoading?: boolean
}

export function AuditoriaList({ logs, isLoading }: AuditoriaListProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Cargando registros...</span>
      </div>
    )
  }

  if (logs.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No hay registros de auditoria
      </div>
    )
  }

  const formatearFecha = (fecha: string) => {
    const d = new Date(fecha)
    return d.toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <TooltipProvider>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[160px]">Fecha</TableHead>
              <TableHead>Usuario</TableHead>
              <TableHead className="w-[120px]">Accion</TableHead>
              <TableHead className="w-[120px]">Modulo</TableHead>
              <TableHead>Detalle</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.map((log) => {
              const accionConfig = getAccionConfig(log.accion)
              const entidadConfig = getEntidadConfig(log.entidad)
              const detalle = formatearDetalleAuditoria(log.detalle)

              return (
                <TableRow key={log.id}>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatearFecha(log.created_at)}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium text-sm">
                        {log.user_name || 'Sin nombre'}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {log.user_email}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={accionConfig.bgClass}>
                      {accionConfig.label}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{entidadConfig.label}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground truncate max-w-[300px]">
                        {detalle}
                      </span>
                      {log.detalle && Object.keys(log.detalle).length > 0 && (
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="h-4 w-4 text-muted-foreground" />
                          </TooltipTrigger>
                          <TooltipContent side="left" className="max-w-[400px]">
                            <pre className="text-xs whitespace-pre-wrap">
                              {JSON.stringify(log.detalle, null, 2)}
                            </pre>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </TooltipProvider>
  )
}

// Componente compacto para mostrar auditoria de una entidad
export function AuditoriaEntidad({
  logs,
  isLoading,
}: {
  logs: AuditLogConUsuario[]
  isLoading?: boolean
}) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (logs.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-4">
        Sin historial de cambios
      </p>
    )
  }

  return (
    <div className="space-y-2">
      {logs.slice(0, 10).map((log) => {
        const accionConfig = getAccionConfig(log.accion)

        return (
          <div
            key={log.id}
            className="flex items-start gap-2 text-sm border-l-2 pl-3 py-1"
            style={{ borderColor: accionConfig.color }}
          >
            <Badge variant="outline" className={`${accionConfig.bgClass} text-xs`}>
              {accionConfig.label}
            </Badge>
            <div className="flex-1 min-w-0">
              <p className="text-muted-foreground truncate">
                {log.user_name || log.user_email}
              </p>
              <p className="text-xs text-muted-foreground">
                {new Date(log.created_at).toLocaleString('es-AR')}
              </p>
            </div>
          </div>
        )
      })}
      {logs.length > 10 && (
        <p className="text-xs text-muted-foreground text-center">
          +{logs.length - 10} registros mas
        </p>
      )}
    </div>
  )
}
